import React, { useEffect } from 'react';
import Clarity from '@microsoft/clarity';
import initHelpHero from 'helphero';
import { initializeSentry } from './lib/sentry';
import { identifyUser, resetUser } from './lib/analytics';
import SentryErrorBoundary from './components/shared/SentryErrorBoundary';
import MaintenanceBanner from './components/shared/MaintenanceBanner';
import { useAuthStore } from './stores/useAuthStore';
import AppRouter from './router/AppRouter';

// Route registry note: `/trendforge` -> TrendForgeWizardPage (lazy-loaded inside AppRouter).
// Kept here so the route is grep-discoverable from the App entry point.

// Clarity Project ID — only set via env var, no hardcoded fallback to avoid prod data pollution
const CLARITY_PROJECT_ID = import.meta.env.VITE_CLARITY_PROJECT_ID || '';

// HelpHero App ID — only set via env var, no hardcoded fallback to avoid prod data pollution
const HELPHERO_APP_ID = import.meta.env.VITE_HELPHERO_APP_ID || '';

// Initialize HelpHero instance (only if App ID is configured)
const helpHero = HELPHERO_APP_ID ? initHelpHero(HELPHERO_APP_ID) : null;

const App: React.FC = () => {
  // Initialize Sentry and Clarity
  useEffect(() => {
    initializeSentry();

    // Initialize Microsoft Clarity for session recordings and heatmaps
    if (CLARITY_PROJECT_ID) {
      Clarity.init(CLARITY_PROJECT_ID);
      // Expose Clarity SDK methods for testing
      (window as any).ClaritySDK = Clarity;
      console.log('[Clarity] Initialized with project:', CLARITY_PROJECT_ID);
    }
  }, []);

  // Update user context across all analytics platforms
  const { user, isAuthenticated } = useAuthStore();
  useEffect(() => {
    if (isAuthenticated && user) {
      // Identify user across PostHog, Sentry, and Clarity
      identifyUser({
        id: user.id.toString(),
        email: user.email,
        name: user.name,
        company: user.company,
        role: user.role,
        creditBalance: user.creditBalance,
        createdAt: user.createdAt,
        avatar: user.avatar,
      });

      // Identify user in HelpHero for personalized tours
      helpHero?.identify(user.id.toString(), {
        name: user.name,
        email: user.email,
        role: user.role,
        created_at: user.createdAt,
      });
    } else {
      // Reset analytics when user logs out
      resetUser();
    }
  }, [isAuthenticated, user]);

  return (
    <SentryErrorBoundary>
      <AppRouter />
    </SentryErrorBoundary>
  );
};

export default App;
